import { Controller } from 'stimulus'
import { AmortizationGraph } from 'classes/amortization-graph'
import { ScrollyAmortizationTable } from 'classes/scrolly-amortization-table'

import * as MortMath from 'modules/mort-math'
import * as MortDataPrep from 'modules/mort-data-prep'
import moment from 'moment'

import * as MomentHelp from 'helpers/moment-helper'
import parseFloatString from 'helpers/parse-float-string'
import formatCurrency from 'helpers/new-format-currency'


/**
 * Manages js for the refinance calculator page (StimulusJS controller)
 *
 * @class RefinanceCalculatorController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'amortizationGraph',
    'amortizationInformationDate',
    'amortizationInformationDateTense',
    'amortizationInformationInterestLosses',
    'amortizationInformationInterestLossesDeterminer',
    'amortizationInformationInterestLossesValue',
    'amortizationInformationInterestSavings',
    'amortizationInformationInterestSavingsValue',
    'amortizationTable',
    'informationNoSavings',
    'informationNoSavingsInterestValue',
    'informationNoSavingsPaymentValue',
    'informationSavingsOnInterestAndPayment',
    'informationSavingsOnInterestAndPaymentInterestValue',
    'informationSavingsOnInterestAndPaymentPaymentValue',
    'informationSavingsOnOnlyInterest',
    'informationSavingsOnOnlyInterestInterestValue',
    'informationSavingsOnOnlyInterestPaymentValue',
    'informationSavingsOnOnlyPayment',
    'informationSavingsOnOnlyPaymentInterestValue',
    'informationSavingsOnOnlyPaymentPaymentValue',
    'lifetimeSavings',
    'lifetimeSavingsContainer',
    'originalMonthlyPayment',
    'monthlySavings',
    'monthlySavingsContainer',
    'newAdditionalMonthlyPayment',
    'newInterestRatePercent',
    'newLoanAmount',
    'newLoanAmount',
    'newLoanStartDate',
    'newLoanTerm',
    'newMonthlyPayment',
    'originalAdditionalMonthlyPayment',
    'originalInterestRatePercent',
    'originalLoanAmount',
    'originalLoanStartDate',
    'originalLoanTerm',
    'payoff',
    'payoffDate',
    'payoffRelativeOccurrence',
    'payoffRelativeOccurrenceContainer',
    'toggleAmortizationTable'
  ]

  timeout = null

  /**
   * @function Controllers.RefinanceCalculatorController.initialize
   * @memberof Controllers
   * @description Called on page load. Calculates both the current and new mortgage payments differences
   */
  initialize() {
    this.termToggle = 'monthly'

    const { originalSchedule, newSchedule, textualData, newStartDate } = this._recalculateData()

    this._displayTextualData(textualData)

    const amortizationGraphData           = this._prepareGraphData(originalSchedule, newSchedule, newStartDate)
    const amortizationComparisonTableData = MortDataPrep.monthlyComparisonTableData(originalSchedule, newSchedule, newStartDate, { useFiltered: true })

    if (this.hasAmortizationGraphTarget) {
      this.amortizationGraph = new AmortizationGraph(this.amortizationGraphTarget, 'Yrs')
      this.amortizationGraph.build(amortizationGraphData)
    }

    if (this.hasAmortizationTableTarget) {
      this.amortizationTable = new ScrollyAmortizationTable(this.amortizationTableTarget)
      this.amortizationTable.buildDeferred(this.amortizationTableTarget, amortizationComparisonTableData)
    }
  }

  /**
   * @function Controllers.RefinanceCalculatorController.process
   * @memberof Controllers
   * @description Called on `keyup` event and `toggle()`. Calculates both the current and new mortgage payments differences
   */
  process() {
    clearTimeout(this.timeout)

    if (this.hasAmortizationTableTarget) {
      this.amortizationTableTarget.dispatchEvent(new CustomEvent('table:collapse'))
    }

    this.timeout = setTimeout(() => {
      if (!this.hasStartDates) { return false }

      const { originalSchedule, newSchedule, textualData, newStartDate } = this._recalculateData()

      this._displayTextualData(textualData)

      const amortizationGraphData = this._prepareGraphData(originalSchedule, newSchedule, newStartDate)

      let amortizationComparisonTableData

      if (this.termToggle === 'annually') {
        const yearlyOriginalSchedule    = MortMath.yearlyAmortizationSchedule(originalSchedule)
        const yearlyNewSchedule         = MortMath.yearlyAmortizationSchedule(newSchedule)
        amortizationComparisonTableData = MortDataPrep.yearlyComparisonTableData(yearlyOriginalSchedule, yearlyNewSchedule, newStartDate, { useFiltered: true })
      } else {
        amortizationComparisonTableData = MortDataPrep.monthlyComparisonTableData(originalSchedule, newSchedule, newStartDate, { useFiltered: true })
      }

      if (this.hasAmortizationGraphTarget) {
        this.amortizationGraph.build(amortizationGraphData)
      }
      if (this.hasAmortizationTableTarget) {
        this.amortizationTable.build(amortizationComparisonTableData)
      }
    }, 300)
  }

  /**
   * @function Controllers.RefinanceCalculatorController.toggle
   * @memberof Controllers
   * @description Toggles graph between monthly and yearly. Called on `click` event
   *
   * @param {Event} event from user interaction
   */
  toggle(event) {
    const toggleButtons = document.querySelectorAll(`[data-target="${this.toggleAmortizationTableTarget.dataset.target}"]`)

    Array.from(toggleButtons).map(button => {
      button.classList.remove('mortgage-calculator__amortization-information-toggle-button--active')
    })

    event.target.classList.add('mortgage-calculator__amortization-information-toggle-button--active')

    // Sets toggle to "monthly" or "annually".
    this.termToggle = event.target.dataset.value

    this.process()
  }

  // private

  /**
   * @function Controllers.RefinanceCalculatorController._recalculateData
   * @memberof Controllers
   * @description A phoenix rises from the ashes of calculations past... Takes inputs from the UI and gets calculations from MortMath
   *
   * @return {Object} original schedule, new schedule, start date, and textual data
   * @private
   * @example
   *
   * return = {
   *   newSchedule: {2018/8: {...}, 2018/8: {...}, ...},
   *   newStartDate: moment,
   *   originalSchedule: {2010/7: {...}, 2010/8: {...}, ...},
   *   textualData: {
   *     lifetimeSavings: float,
   *     yearDiff: integer,
   *     monthDiff: integer,
   *     paymentDiff: float,
   *     originalPayment: float,
   *     newPayment: float,
   *   },
   * }
   *
   */
  _recalculateData() {
    const { originalDetails, originalMonthlyPayment } = this._prepareFormValuesForOriginal()
    const originalSchedule = MortMath.amortizationSchedule(originalDetails)

    const { newDetails, newMonthlyPayment, newStartDate } = this._prepareFormValuesForNew()
    const newSchedule = MortMath.amortizationSchedule(newDetails)

    const originalNewPaymentDiff = originalMonthlyPayment - newMonthlyPayment

    const originalTotalInterestFromNewStart = MortMath.totalInterestFrom(originalSchedule, newDetails.date)
    const newTotalInterestOverall = MortMath.totalInterest(newSchedule)

    const lifetimeSavings = originalTotalInterestFromNewStart - newTotalInterestOverall

    const { yearDiff, monthDiff } = MortDataPrep.loanEndDifference(originalSchedule, newSchedule)

    return {
      textualData: {
        lifetimeSavings: lifetimeSavings,
        yearDiff:        yearDiff,
        monthDiff:       monthDiff,
        paymentDiff:     originalNewPaymentDiff,
        originalPayment: originalMonthlyPayment,
        newPayment:      newMonthlyPayment
      },
      originalSchedule: originalSchedule,
      newSchedule:      newSchedule,
      newStartDate:     newStartDate
    }
  }

  /**
   * @function Controllers.RefinanceCalculatorController._prepareFormValuesForOriginal
   * @memberof Controllers
   * @description Takes inputs from the original loan form and prepares them for MortMath consumption
   *
   * @return {Object} - originalDetails and originalMonthlyPayment
   * @private
   * @example
   *
   * return = {
   *   originalDetails: {
   *     additionalPaymentAmount: 0,
   *     additionalPaymentStart: moment,
   *     balance: 300000,
   *     date: moment,
   *     monthlyInterestPercent: 0.00333333333...,
   *     monthlyPayment: 1432.2458...,
   *   },
   *   originalMonthlyPayment: 1432.2458...
   * }
   */
  _prepareFormValuesForOriginal() {
    const parsedOriginalAdditionalPayment = parseFloatString(this.originalAdditionalMonthlyPaymentTarget.value) || 0
    const parsedOriginalInterestPercent   = parseFloatString(this.originalInterestRatePercentTarget.value)
    const parsedOriginalLoanAmount        = parseFloatString(this.originalLoanAmountTarget.value)
    const parsedOriginalLoanTerm          = parseFloatString(this.originalLoanTermTarget.value)
    const parsedOriginalloanStartDate     = MomentHelp.formatForMomentMonthFirst(this.originalLoanStartDateTarget.value)

    const originalAddtionalPaymentStart  = moment()
    const originalLoanTermInMonths       = parsedOriginalLoanTerm * 12
    const originalMonthlyInterestPercent = MortMath.monthlyInterestPercent(parsedOriginalInterestPercent)
    const originalMonthlyPayment         = MortMath.monthlyPayment({
      principal:              parsedOriginalLoanAmount,
      monthlyInterestPercent: originalMonthlyInterestPercent,
      loanTermInMonths:       originalLoanTermInMonths
    })

    const originalDetails = {
      date:                    moment(parsedOriginalloanStartDate),
      monthlyInterestPercent:  originalMonthlyInterestPercent || 0,
      monthlyPayment:          originalMonthlyPayment || 0,
      balance:                 parsedOriginalLoanAmount || 0,
      additionalPaymentStart:  originalAddtionalPaymentStart,
      additionalPaymentAmount: parsedOriginalAdditionalPayment || 0
    }

    const originalAdditionalAdjustedMonthlyPayment = originalMonthlyPayment + parsedOriginalAdditionalPayment

    return { originalDetails: originalDetails, originalMonthlyPayment: originalAdditionalAdjustedMonthlyPayment }
  }

  /**
   * @function Controllers.RefinanceCalculatorController._prepareFormValuesForNew
   * @memberof Controllers
   * @description Takes inputs from the new loan form and prepares them for MortMath consumption
   *
   * @return {Object}
   * @private
   * @example
   * return = {
   *   newDetails: {
   *     additionalPaymentAmount: 0,
   *     additionalPaymentStart: moment,
   *     balance: 300000,
   *     date: moment,
   *     monthlyInterestPercent: 0.00333333333...,
   *     monthlyPayment: 1432.2458...,
   *   },
   *   newMonthlyPayment: 1432.2458...,
   *   newStartDate: moment
   * }
   */
  _prepareFormValuesForNew() {
    const parsedNewAdditionalPayment = parseFloatString(this.newAdditionalMonthlyPaymentTarget.value) || 0
    const parsedNewInterestPercent   = parseFloatString(this.newInterestRatePercentTarget.value)
    const parsedNewLoanAmount        = parseFloatString(this.newLoanAmountTarget.value)
    const parsedNewLoanTerm          = parseFloatString(this.newLoanTermTarget.value)
    const parsedNewLoanStartDate     = MomentHelp.formatForMomentMonthFirst(this.newLoanStartDateTarget.value)

    const newAddtionalPaymentStart   = moment()
    const newLoanTermInMonths        = (parsedNewLoanTerm || 0) * 12
    const newMonthlyInterestPercent  = MortMath.monthlyInterestPercent(parsedNewInterestPercent)
    const newMonthlyPayment          = MortMath.monthlyPayment({
      principal:              parsedNewLoanAmount,
      monthlyInterestPercent: newMonthlyInterestPercent,
      loanTermInMonths:       newLoanTermInMonths
    })

    const newStartDate = moment( parsedNewLoanStartDate )

    const newDetails = {
      date:                    newStartDate,
      monthlyInterestPercent:  newMonthlyInterestPercent || 0,
      monthlyPayment:          newMonthlyPayment || 0,
      balance:                 parsedNewLoanAmount || 0,
      additionalPaymentStart:  newAddtionalPaymentStart,
      additionalPaymentAmount: parsedNewAdditionalPayment || 0
    }

    const newAdditionalAdjustedMonthlyPayment = newMonthlyPayment + parsedNewAdditionalPayment

    return { newDetails: newDetails, newMonthlyPayment: newAdditionalAdjustedMonthlyPayment, newStartDate: newStartDate }
  }


  /**
   * @function Controllers.RefinanceCalculatorController._prepareGraphData
   * @memberof Controllers
   * @description Returns formatted data from originalSchedule and newSchedule suitable for d3 graph
   *
   * @param {Object} originalSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   * @param {Object} newSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   * @param {moment} newStartDate - moment date Object representing new loan start date
   *
   * @return {Array.<Object>} labels and data for 'Original Loan', 'New Loan', and 'Yrs'
   * @private
   * @example
   *
   * returns = [
   *   {
   *     label: "Yrs",
   *     data: [ 0, 1, 2, ... ],
   *   },
   *   {
   *     label: "Original Loan",
   *     data: [ 246984.9614993914, 239541.93773316863, 231795.6736947213, ... ],
   *   },
   *   {
   *     label: "New Loan",
   *     data: [ 238579.07095882075, 235042.72320823427, 231320.8192421315, ... ]
   *    },
   * ]
   */
  _prepareGraphData(originalSchedule, newSchedule, newStartDate) {
    const originalYearlyData = MortMath.attributeOfInterestByYearFrom(originalSchedule, newStartDate, 'balance')
    const newYearlyData = MortMath.attributeOfInterestByYearFrom(newSchedule, newStartDate, 'balance')

    return MortDataPrep.graphComparisonData(originalYearlyData, newYearlyData, newStartDate)
  }

  /**
   * @function Controllers.RefinanceCalculatorController._displayTextualData
   * @memberof Controllers
   * @description Displays the textual calculated results
   *
   * @param {Object} textData
   * @param {Float} textData.lifetimeSavings
   * @param {Integer} textData.yearDiff
   * @param {Integer} textData.monthDiff
   * @param {Float} textData.paymentDiff
   * @param {Float} textData.newPayment
   * @param {Float} textData.originalPayment
   *
   * @private
   */
  _displayTextualData({ lifetimeSavings, yearDiff, monthDiff, paymentDiff, newPayment, originalPayment }) {
    // Show & hide result information text based on result values
    this._displayResultsInformation(paymentDiff, lifetimeSavings, monthDiff, yearDiff)

    // Displays results values
    this.originalMonthlyPaymentTarget.innerHTML   = formatCurrency(originalPayment)
    this.newMonthlyPaymentTarget.innerHTML        = formatCurrency(newPayment)
    this.monthlySavingsTarget.innerHTML           = formatCurrency(paymentDiff) || '$0'
    this.lifetimeSavingsTarget.innerHTML          = formatCurrency(lifetimeSavings)
    this.payoffTarget.innerHTML                   = `${Math.abs(yearDiff)} yrs and ${Math.abs(monthDiff)} mos.`
    this.payoffRelativeOccurrenceTarget.innerHTML = (monthDiff < 0 || yearDiff < 0) ? 'later' : 'earlier'

    this._visualizeSavingsFor(
      this.monthlySavingsContainerTarget,
      paymentDiff,
      'mortgage-calculator__information-box--green',
      'mortgage-calculator__information-box--red'
    )
    this._visualizeSavingsFor(
      this.lifetimeSavingsContainerTarget,
      lifetimeSavings,
      'mortgage-calculator__information-box--green',
      'mortgage-calculator__information-box--red'
    )
    this._visualizeSavingsFor(
      this.payoffRelativeOccurrenceContainerTarget,
      monthDiff,
      'mortgage-calculator__information-box--green',
      'mortgage-calculator__information-box--red'
    )
  }

  /**
   * @function Controllers.RefinanceCalculatorController._visualizeSavingsFor
   * @memberof Controllers
   * @description Applies a style class to the target depending on if the value is negative or positive
   * @private
   *
   * @param {Element} target
   * @param {number} value
   * @param {String} positiveClassModifier - Style class to apply when the `value` is positive
   * @param {String} negativeClassModifier - Style class to apply when the `value` is negative
   */
  _visualizeSavingsFor(target, value, positiveClassModifier, negativeClassModifier) {
    if (0 <= value) {
      target.classList.remove(negativeClassModifier)
      target.classList.add(positiveClassModifier)
    } else {
      target.classList.remove(positiveClassModifier)
      target.classList.add(negativeClassModifier)
    }
  }


  /**
   * @function Controllers.RefinanceCalculatorController._displayResultsInformation
   * @memberof Controllers
   * @description Shows the related results information depending on the positive or negative values for monthly difference or
   * interested saved
   * @private
   *
   * @param {Float} paymentDiff
   * @param {Float} lifetimeSavings - Savings on interest for the life of the loan
   * @param {Integer} monthDiff
   * @param {Integer} yearDiff
   */
  _displayResultsInformation(paymentDiff, lifetimeSavings, monthDiff, yearDiff) {
    const formattedMonthlyDifference = formatCurrency(Math.abs(paymentDiff)) || '$0'
    const formattedLifetimeSavings = formatCurrency(Math.abs(lifetimeSavings)) || '$0'

    this._hideResultsInformation()

    if (paymentDiff > 0 && lifetimeSavings > 0) {
      // If both monthly difference and lifetime savings are positive

      this.informationSavingsOnInterestAndPaymentTarget.style.display = ''
      if (this.hasAmortizationInformationInterestLossesTarget) {
        this.amortizationInformationInterestLossesTarget.style.display = ''
      }
    } else if (paymentDiff <= 0 && lifetimeSavings > 0) {
      // If monthly difference is negative or 0 and lifetime savings are positive

      this.informationSavingsOnOnlyInterestTarget.style.display = ''
      if (this.hasAmortizationInformationInterestLossesTarget) {
        this.amortizationInformationInterestLossesTarget.style.display = ''
      }
    } else if (paymentDiff > 0 && lifetimeSavings <= 0) {
      // If monthly savings is positive and lifetime savings is negative or 0

      this.informationSavingsOnOnlyPaymentTarget.style.display = ''
      if (this.hasAmortizationInformationInterestLossesTarget) {
        this.amortizationInformationInterestLossesTarget.style.display = ''
      }
    } else {
      this.informationNoSavingsTarget.style.display = ''
      if (this.hasAmortizationInformationInterestLossesTarget) {
        this.amortizationInformationInterestLossesTarget.style.display = ''
      }
    }

    if (this.hasAmortizationInformationDateTenseTarget) {
      this.amortizationInformationDateTenseTarget.textContent = (monthDiff < 0 || yearDiff < 0) ? 'later' : 'earlier'
    }

    if (this.hasAmortizationInformationInterestLossesDeterminerTarget) {
      if (lifetimeSavings < 0) {
        this.amortizationInformationInterestLossesDeterminerTarget.innerHTML = 'more'
      } else if (lifetimeSavings === 0) {
        this.amortizationInformationInterestLossesDeterminerTarget.innerHTML = ''
      } else {
        this.amortizationInformationInterestLossesDeterminerTarget.innerHTML = 'less'
      }
    }

    const yearTense  = yearDiff === 1 ? 'year' : 'years'
    const monthTense = monthDiff === 1 ? 'month' : 'months'

    const stringDate = `${Math.abs(yearDiff)} ${yearTense} and ${Math.abs(monthDiff)} ${monthTense}`

    // Set values to all of the information result targets
    this.informationNoSavingsInterestValueTarget.innerHTML                   = formattedLifetimeSavings
    this.informationNoSavingsPaymentValueTarget.innerHTML                    = formattedMonthlyDifference
    this.informationSavingsOnInterestAndPaymentInterestValueTarget.innerHTML = formattedLifetimeSavings
    this.informationSavingsOnInterestAndPaymentPaymentValueTarget.innerHTML  = formattedMonthlyDifference
    this.informationSavingsOnOnlyInterestInterestValueTarget.innerHTML       = formattedLifetimeSavings
    this.informationSavingsOnOnlyInterestPaymentValueTarget.innerHTML        = formattedMonthlyDifference
    this.informationSavingsOnOnlyPaymentInterestValueTarget.innerHTML        = formattedLifetimeSavings
    this.informationSavingsOnOnlyPaymentPaymentValueTarget.innerHTML         = formattedMonthlyDifference

    // Set values to all of the information amortization targets
    if (this.hasAmortizationInformationInterestSavingsValueTarget) {
      this.amortizationInformationInterestSavingsValueTarget.innerHTML = formattedLifetimeSavings
    }
    if (this.hasAmortizationInformationInterestLossesValueTarget) {
      this.amortizationInformationInterestLossesValueTarget.innerHTML  = formattedLifetimeSavings
    }
    if (this.hasAmortizationInformationDateTarget) {
      this.amortizationInformationDateTarget.innerText                 = stringDate
    }
  }


  /**
   * @function Controllers.RefinanceCalculatorController._hideResultsInformation
   * @memberof Controllers
   *
   * @description Sets the style display to all information options
   *
   * @private
   */
  _hideResultsInformation() {
    if (this.hasAmortizationInformationInterestLossesTarget) {
      this.amortizationInformationInterestLossesTarget.style.display  = 'none'
    }
    if (this.hasAmortizationInformationInterestSavingsTarget) {
      this.amortizationInformationInterestSavingsTarget.style.display = 'none'
    }
    this.informationNoSavingsTarget.style.display                   = 'none'
    this.informationSavingsOnInterestAndPaymentTarget.style.display = 'none'
    this.informationSavingsOnOnlyInterestTarget.style.display       = 'none'
    this.informationSavingsOnOnlyPaymentTarget.style.display        = 'none'
  }


  /**
  * @function Controllers.RefinanceCalculatorController.hasStartDates
  * @memberof Controllers
  * @description Validates that newLoanStartDate and originalLoanStartDate have 7 digits.
  * @private
  *
  * @return {boolean}
  */
  get hasStartDates() {
    return this.newLoanStartDateTarget.value.length === 7 &&
           this.originalLoanStartDateTarget.value.length === 7
  }
}
